import { useState } from 'react'
import { orpc } from '@/lib/orpc-client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface CreateOrgDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (orgId: string) => void
}

export function CreateOrgDialog({ open, onOpenChange, onCreated }: CreateOrgDialogProps) {
  const [name, setName] = useState('')
  const [projectLimit, setProjectLimit] = useState(5)
  const [signupMode, setSignupMode] = useState<'invite_only' | 'domain_restricted'>('invite_only')
  const [allowedEmailDomain, setAllowedEmailDomain] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resetForm = () => {
    setName('')
    setProjectLimit(10)
    setSignupMode('invite_only')
    setAllowedEmailDomain('')
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsCreating(true)

    try {
      const res = await orpc.admin.createOrganization({
        name: name.trim(),
        projectLimit,
        signupMode,
        allowedEmailDomain:
          signupMode === 'domain_restricted' ? allowedEmailDomain.trim() || null : null,
      })
      resetForm()
      onCreated(res.organization.id)
    } catch {
      setError('Could not create organization. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetForm()
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>Fill in the details for the new organization.</DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
          <div className="space-y-2">
            <Label htmlFor="create-org-name">Name</Label>
            <Input
              id="create-org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Organization name"
              required
              maxLength={100}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="create-org-limit">Project limit</Label>
            <Input
              id="create-org-limit"
              type="number"
              min={1}
              value={projectLimit}
              onChange={(e) => setProjectLimit(Number(e.target.value))}
              required
            />
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-foreground">Signup mode</legend>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="createSignupMode"
                value="invite_only"
                checked={signupMode === 'invite_only'}
                onChange={() => setSignupMode('invite_only')}
                className="accent-primary"
              />
              <span className="text-sm">Invite only</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="createSignupMode"
                value="domain_restricted"
                checked={signupMode === 'domain_restricted'}
                onChange={() => setSignupMode('domain_restricted')}
                className="accent-primary"
              />
              <span className="text-sm">Domain restricted</span>
            </label>
          </fieldset>

          {signupMode === 'domain_restricted' && (
            <div className="space-y-2">
              <Label htmlFor="create-org-domain">Allowed email domain</Label>
              <Input
                id="create-org-domain"
                placeholder="e.g. skanska.se"
                value={allowedEmailDomain}
                onChange={(e) => setAllowedEmailDomain(e.target.value)}
              />
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || !name.trim()}>
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
